import { Box, FormControlLabel, Switch } from '@mui/material';
import type { ReactNode } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CoreStoreProvider } from '@opengeoweb/core';
import type {
  Coverage,
  GridDomain,
  PointSeriesDomain,
} from '@opengeoweb/shared';
import { SessionStorageKey } from '@opengeoweb/shared';
import { createMockStore } from '@opengeoweb/store';
import { ThemeProvider } from '@opengeoweb/theme';
import IntegratedDensity from './flysafe/components/IntegratedDensity';
import {
  getCachedModelForStation,
  getCachedModelSZAForStation,
  getCachedStationLocations,
  getCachedStationCoverageForPosition,
} from './flysafe/edr-api/cachedApi';
import { coverageToRowsGrid } from './flysafe/utils/coverageToRowsGrid';
import { DAY_MS, HOUR_MS } from './flysafe/utils/timeUtils';
import type { Mode, FlySafeRow } from './flysafe/utils/types';

const HERWIJNEN_COORDS: [number, number] = [5.138, 51.837];

interface Props {
  id: number;
  timeWindow: [Date, Date];
  stationId?: string;
  coverage?: Coverage<PointSeriesDomain>;
  observedCoverage?: Coverage<GridDomain>;
  szaCoverage?: Coverage<PointSeriesDomain>;
}

function coverageToRows(
  cov?: Coverage<PointSeriesDomain>,
): FlySafeRow[] | null {
  if (!cov) {
    return null;
  }

  const { axes } = cov.domain;
  const tVals = axes.t?.values as string[];
  const yCount = axes.y.values.length;
  const xCount = axes.x.values.length;

  const dens = cov.ranges.dens?.values as number[] | undefined;

  if (!tVals?.length) {
    return null;
  }
  if (!dens?.length) {
    return null;
  }

  const tCount = tVals.length;

  const out: FlySafeRow[] = [];
  out.length = tCount * 2 * yCount * xCount;
  // Atificial KM height values to make plotting work later
  const zVals = [0, 1];

  let ki = -1;
  for (let ti = 0; ti < tCount; ti += 1) {
    for (let zi = 0; zi < 2; zi += 1) {
      for (let yi = 0; yi < yCount; yi += 1) {
        for (let xi = 0; xi < xCount; xi += 1) {
          const idx = (ti * yCount + yi) * xCount + xi;

          out[(ki += 1)] = {
            datetime: new Date(tVals[ti]),
            height: zVals[zi],
            dens: dens[idx] ?? null,
            ff: null,
            dd: null,
          };
        }
      }
    }
  }

  return out;
}

interface DisplayState {
  rows: FlySafeRow[] | null;
}

const FlySafeModel = (props: Props): ReactNode => {
  const { id, timeWindow, stationId, coverage, szaCoverage, observedCoverage } =
    props;

  // Data used by both plots (heat map and integrated density)
  // SZA is further processed in the integrated density plot
  const rowsFromCoverage = useMemo(() => coverageToRows(coverage), [coverage]);

  const observedRowsRaw = useMemo(
    () => coverageToRowsGrid(observedCoverage),
    [observedCoverage],
  );

  const [display, setDisplay] = useState<DisplayState>(() => ({
    rows: coverageToRows(coverage),
  }));

  const [mode, setMode] = useState<Mode>(() => 'density' as Mode);

  const handleModeSwitch = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setMode(checked ? 'birdtam' : 'density');
    },
    [],
  );

  const covKey = useMemo(() => {
    const tVals = coverage?.domain.axes.t.values as string[] | undefined;
    const first = tVals?.[0] ?? '';
    const last = tVals?.[tVals.length - 1] ?? '';
    const len = tVals?.length ?? 0;
    return `${stationId ?? ''}|${len}|${first}|${last}`;
  }, [coverage, stationId]);

  const prevCovKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!coverage) {
      return;
    }
    if (prevCovKeyRef.current === covKey) {
      return;
    }
    prevCovKeyRef.current = covKey;

    setDisplay({ rows: rowsFromCoverage });
  }, [coverage, covKey, rowsFromCoverage]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          pb: 1.5,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={mode === 'birdtam'}
              color="primary"
              onChange={handleModeSwitch}
            />
          }
          label={mode === 'birdtam' ? 'BirdTAM' : 'Density'}
          sx={{ marginLeft: 'auto', marginRight: 0 }}
        />
      </Box>

      <Box>
        <IntegratedDensity
          forecastData={display.rows}
          id={id}
          mode={mode}
          observedData={observedRowsRaw}
          preferredHeight="40vh"
          stationId={stationId}
          szaCoverage={szaCoverage}
          timeWindow={timeWindow}
        />
      </Box>
    </Box>
  );
};

const FlySafeForecast: React.FC = () => {
  const store = React.useMemo(() => createMockStore(), []);

  sessionStorage.setItem(
    SessionStorageKey.CONFIG,
    JSON.stringify({
      GW_FLYSAFE_STATION_DATA_URL: `${window.location.origin.startsWith('http://localhost') ? 'https://flysafe.radar.acc.knmi.cloud' : window.location.origin}`,
      GW_FLYSAFE_SOLAR_TERMINATOR_URL: `${window.location.origin.startsWith('http://localhost') ? 'https://flysafe.radar.acc.knmi.cloud' : window.location.origin}`,
    }),
  );
  const modelStationId = 'nlhrw';
  const [minTime, setMinTime] = useState<Date>(new Date());
  const [maxTime, setMaxTime] = useState<Date>(new Date());

  const [coverage, setCoverage] =
    React.useState<Coverage<PointSeriesDomain> | null>(null);
  const [szaCoverage, setSzaCoverage] =
    React.useState<Coverage<PointSeriesDomain> | null>(null);
  const [observedCoverage, setObservedCoverage] =
    React.useState<Coverage<GridDomain> | null>(null);
  // Turns true once the fetch sequence has finished (whether individual
  // requests succeeded or failed). Used purely to distinguish "still
  // loading" from "finished, but some data missing" so the UI can render
  // with whatever data came back instead of staying blank forever.
  const [loaded, setLoaded] = useState<boolean>(false);

  const fetchAndUpsertCoverage = useCallback(async () => {
    try {
      const stationLocations = await getCachedStationLocations();
      const feature = stationLocations.features.find(
        (feat) => String(feat.id) === modelStationId,
      );
      const props = feature?.properties as Record<string, string> | undefined;
      const latestModelTimestamp = props?.latest_model_timestamp;
      if (!latestModelTimestamp) {
        throw new Error('latest_model_timestamp not available');
      }
      const from = new Date(
        new Date(latestModelTimestamp).getTime() - 5 * DAY_MS - 90 * HOUR_MS,
      );
      const to = new Date(latestModelTimestamp);
      setMinTime(from);
      setMaxTime(to);
      // Fetch the three coverages independently so a single failure does
      // not hide the data that did load. Each rejection is logged; the
      // corresponding state simply stays null.
      const [covResult, szaResult, observedResult] = await Promise.allSettled([
        getCachedModelForStation(modelStationId, latestModelTimestamp),
        getCachedModelSZAForStation(modelStationId, latestModelTimestamp),
        getCachedStationCoverageForPosition(
          HERWIJNEN_COORDS,
          7,
          new Date(latestModelTimestamp),
        ),
      ]);
      if (covResult.status === 'fulfilled') {
        setCoverage(covResult.value);
      } else {
        console.error('Failed to fetch model coverage:', covResult.reason);
      }
      if (szaResult.status === 'fulfilled') {
        setSzaCoverage(szaResult.value);
      } else {
        console.error('Failed to fetch model SZA coverage:', szaResult.reason);
      }
      if (observedResult.status === 'fulfilled') {
        setObservedCoverage(observedResult.value);
      } else {
        console.error(
          'Failed to fetch observed station coverage:',
          observedResult.reason,
        );
      }
    } catch (err) {
      // A failure here (e.g. getCachedStationLocations unavailable or no
      // latest_model_timestamp) means we cannot derive the time window or
      // kick off the dependent fetches. Log and continue; the UI will
      // still render its chrome without any data.
      console.error('EDR position error:', err);
    } finally {
      setLoaded(true);
    }
  }, [setCoverage]);

  useEffect(() => {
    void fetchAndUpsertCoverage();
  }, [fetchAndUpsertCoverage]);

  // Keep a minimal loading placeholder so we do not flash an empty chart
  // before the first fetch has had a chance to resolve.
  if (!loaded) {
    return <div />;
  }

  return (
    <ThemeProvider>
      <CoreStoreProvider store={store}>
        <FlySafeModel
          coverage={coverage ?? undefined}
          id={0}
          observedCoverage={observedCoverage ?? undefined}
          stationId={modelStationId}
          szaCoverage={szaCoverage ?? undefined}
          timeWindow={[new Date(minTime), new Date(maxTime)]}
        />
      </CoreStoreProvider>
    </ThemeProvider>
  );
};

export default FlySafeForecast;
